/**
 * The last place landmarks exist before readiness becomes numbers.
 *
 * `bodyReadingsFrom` takes a {@link PoseFrame} and returns four scalars. Everything above this
 * file — the camera session's snapshot, the readiness evaluator, the React screen — sees only
 * those scalars, so "the shell never touches raw landmarks" is a property of the types rather
 * than of anybody's restraint (architecture §11, §25).
 *
 * Nothing here is persisted or transmitted: readings are computed per frame and dropped.
 */

import { CORE_POSE_LANDMARKS, type PoseFrame, type PoseLandmarkPoint } from '../pose';
import type { BodyReadings } from './readiness';

/**
 * Mean relative luminance of an RGBA buffer, 0–1, or null for an empty buffer.
 *
 * Rec. 709 coefficients rather than a flat channel average: a room lit by a warm lamp and a room
 * lit by a blue screen are not equally bright to a camera's exposure or to the pose model, and a
 * flat average would call the blue one brighter than it looks. Alpha is ignored — a video frame
 * is opaque.
 */
export function meanLuminanceFromRgba(data: Uint8ClampedArray): number | null {
  const pixels = Math.floor(data.length / 4);
  if (pixels === 0) return null;

  let total = 0;
  for (let index = 0; index < pixels; index += 1) {
    const offset = index * 4;
    total +=
      0.2126 * (data[offset] ?? 0) +
      0.7152 * (data[offset + 1] ?? 0) +
      0.0722 * (data[offset + 2] ?? 0);
  }

  return total / pixels / 255;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Measures the body the readiness screen is asking about.
 *
 * Apparent height is measured over the landmarks that are *confidently visible*, because that is
 * the body the game can actually read: a child whose legs are under a table is shorter to the
 * detectors than they are in the room, and reporting the wishful number would recommend a mode
 * the detectors cannot support. When almost nothing is visible the span falls back to every core
 * landmark, so "we can see something, badly" stays distinguishable from "nobody is there".
 *
 * `minConfidence` is the flagged D-060(5) gate (`pose.minCoreConfidence`), passed in rather than
 * read here so the config layer stays the single source (D-010).
 */
export function bodyReadingsFrom(frame: PoseFrame, minConfidence: number): BodyReadings | null {
  const pose = [...frame.poses].sort((a, b) => b.coreConfidence - a.coreConfidence)[0];
  if (pose === undefined) return null;

  const core = CORE_POSE_LANDMARKS.map((index) => pose.landmarks[index]).filter(
    (point): point is PoseLandmarkPoint => point !== undefined,
  );
  if (core.length === 0) return null;

  const visible = core.filter((point) => point.visibility >= minConfidence);
  const measured = visible.length >= 2 ? visible : core;

  const ys = measured.map((point) => clamp01(point.y));
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);

  return {
    heightFraction: bottom - top,
    visibleCoreFraction: visible.length / core.length,
    topEdgeGap: top,
    coreConfidence: pose.coreConfidence,
  };
}
